import { useEffect, useState } from 'react';
import { 
	SimpleToastWrapper,
	SimpleToastText,
	CloseButton,
	IconWrapper,
	SimpleToastContent,
	SimpleToastContentWrap,
} from './SimpleToast.styled';
import { Icon } from '../icon/Icon';
import type { IconName } from '../icon';

const TOAST_DISPLAY_DURATION_MS = 3000;
const ANIMATION_DURATION_MS = 300;

interface Props {
	text: string;
	variant: 'info' | 'warning' | 'danger' | 'success';
	headline: string;
	isVisible?: boolean;
	onHide?: () => void;
}

type IconMapping = {
	success: IconName;
	info: IconName;
	warning: IconName;
	danger: IconName;
};

const iconMapping: IconMapping = {
	success: 'circle-check',
	info: 'checkmark-clean',
	warning: 'circle-cross',
	danger: 'circle-cross',
} as const;

export const SimpleToast = ({ 
	text,
	variant,
	headline,
	isVisible = false,
	onHide,
}: Props) => {
	const [show, setShow] = useState(isVisible);
	const [isExiting, setIsExiting] = useState(false);
	const [isHovered, setIsHovered] = useState(false);

	useEffect(() => {
		if (isVisible) {
			setShow(true);
			setIsExiting(false);

			let timeoutId: ReturnType<typeof setTimeout>;
			let startTime = Date.now();
			let remainingTime = TOAST_DISPLAY_DURATION_MS;

			const checkTimer = () => {
				if (!isHovered) {
					const currentTime = Date.now();
					const elapsedTime = currentTime - startTime;
					remainingTime -= elapsedTime;
					
					if (remainingTime <= 0) {
						setIsExiting(true);
						setTimeout(() => {
							setShow(false);
							onHide?.();
						}, ANIMATION_DURATION_MS);
						return;
					}
				}
				
				startTime = Date.now();
				timeoutId = setTimeout(checkTimer, 100);
			};

			timeoutId = setTimeout(checkTimer, 100);

			return () => {
				if (timeoutId) {
					clearTimeout(timeoutId);
				}
			};
		}
		return undefined;
	}, [isVisible, isHovered, onHide]);

	const handleMouseEnter = () => {
		setIsHovered(true);
	};

	const handleMouseLeave = () => {
		setIsHovered(false);
	};

	const handleClose = () => {
		setIsExiting(true);
		setTimeout(() => {
			setShow(false);
			onHide?.();
		}, ANIMATION_DURATION_MS);
	};

	const getIcon = (): IconName => {
		return iconMapping[variant];
	};

	if (!show) return null;

	return (
		<SimpleToastWrapper 
			$variant={variant} 
			$isExiting={isExiting}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			<SimpleToastContent>
				<IconWrapper $variant={variant}>
					<Icon icon={getIcon()} />
				</IconWrapper>
				<SimpleToastContentWrap>{headline} <SimpleToastText>{text}</SimpleToastText></SimpleToastContentWrap>
			</SimpleToastContent>
			<CloseButton onClick={handleClose} $variant={variant}>
				<Icon icon="x-slim" />
			</CloseButton>
		</SimpleToastWrapper>
	);
};
